/**
 * JobMockAI — User Menu Component
 *
 * Avatar with user initials (or photo) in the top bar.
 * Dropdown: Profile, Settings, Billing, Refer Friends, Logout.
 * Shows current plan badge and XP level.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import {
  Person,
  Settings,
  CreditCard,
  GroupAdd,
  Logout,
  WorkOutline,
  ArrowDropDown,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';
import { consumeOAuthPostAuthRedirect } from '../../utils/oauthPostAuthRedirect';
import useInterviewerAccess from '../../hooks/useInterviewerAccess';

const UserMenu = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showBecomeInterviewer, canAccessDashboard } = useInterviewerAccess();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const go = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
    } catch {
      // Logout might fail if the server is unreachable, still redirect
    }
    consumeOAuthPostAuthRedirect();
    navigate('/login', { replace: true });
  };

  // Build initials from user name or email
  const initials = (() => {
    if (!user) return '?';
    if (user.name) {
      return user.name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return (user.email || '?')[0].toUpperCase();
  })();

  const planLabel = user?.plan || user?.subscription_plan || 'Free';

  const menuAvatarSrc = resolveMediaUrl(
    user?.avatar_url ||
      user?.profilePicture ||
      user?.avatar ||
      user?.avatarUrl ||
      ''
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label="User menu"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.15,
          px: { xs: 0.75, md: 1.25 },
          py: 0.25,
          minWidth: { md: 56 },
          border: 'none',
          bgcolor: 'transparent',
          color: 'text.secondary',
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            color: 'text.primary',
            bgcolor: 'action.hover',
          },
        }}
      >
        <Avatar
          src={menuAvatarSrc}
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            bgcolor: 'primary.main',
          }}
        >
          {initials}
        </Avatar>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            lineHeight: 1,
          }}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            Me
          </Typography>
          <ArrowDropDown sx={{ fontSize: 16, ml: -0.25 }} />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { width: 240, mt: 1 } }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name || user?.email || 'User'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip label={planLabel} size="small" color="primary" variant="outlined" />
          </Box>
        </Box>

        <Divider />

        {showBecomeInterviewer && (
          <MenuItem onClick={() => go('/marketplace/apply')}>
            <ListItemIcon><WorkOutline fontSize="small" /></ListItemIcon>
            <ListItemText>Become an Interviewer</ListItemText>
          </MenuItem>
        )}
        {canAccessDashboard && !user?.is_interviewer && (
          <MenuItem onClick={() => go('/interviewer/dashboard')}>
            <ListItemIcon><WorkOutline fontSize="small" /></ListItemIcon>
            <ListItemText>Interviewer Dashboard</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => go('/profile')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => go('/settings')}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => go('/billing')}>
          <ListItemIcon><CreditCard fontSize="small" /></ListItemIcon>
          <ListItemText>Billing</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => go('/referrals')}>
          <ListItemIcon><GroupAdd fontSize="small" /></ListItemIcon>
          <ListItemText>Refer Friends</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            Sign Out
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
